import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeroInView, setIsHeroInView] = useState(false);
  const [isVirtualGolfInView, setIsVirtualGolfInView] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 0);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const targets = Array.from(document.querySelectorAll('[data-hero="true"], #virtual-golf'));
    if (targets.length === 0) {
      setIsHeroInView(false);
      setIsVirtualGolfInView(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const heroVisible = entries.some((entry) => (
          entry.isIntersecting && entry.intersectionRatio > 0.15 && entry.target instanceof HTMLElement && entry.target.dataset.hero === 'true'
        ));
        const golfVisible = entries.some((entry) => (
          entry.isIntersecting && entry.intersectionRatio > 0.1 && entry.target instanceof HTMLElement && entry.target.id === 'virtual-golf'
        ));
        setIsHeroInView(heroVisible);
        setIsVirtualGolfInView(golfVisible);
      },
      { root: null, threshold: [0, 0.1, 0.15, 0.5, 0.75, 1] }
    );

    targets.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [location.pathname]);

  const solidClasses = 'bg-slate-900/95 backdrop-blur-sm border-b border-gray-800/50';
  const headerBackgroundClasses = isVirtualGolfInView
    ? 'bg-transparent'
    : isScrolled
      ? solidClasses
      : isHeroInView
        ? 'bg-transparent'
        : solidClasses;

  const services = [
    'Corporate Services',
    'Human Resource Management Services',
    'Accounting Services',
    'Social Media Presence Management',
    'Co-working Spaces'
  ];

  return (
    <header className={`fixed w-full top-0 z-50 transition-all duration-500 ${headerBackgroundClasses}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 sm:py-6">
          {/* Logo */}
          <div className="flex items-center">
            <h1 className="text-lg sm:text-xl font-AfacadFluxLight font-bold text-white tracking-tight">
              VENTURECORP
              <span className="text-purple-400">.</span>
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-12 uppercase tracking-wider font-bold font-AfacadFluxLight">
            <Link to="/" className="text-white hover:text-purple-300 transition-colors duration-300 font-medium">
              Home
            </Link>
            <Link to="/about" className="text-white hover:text-purple-300 transition-colors duration-300 font-medium">
              About Us
            </Link>
            
            {/* Services with Dropdown */}
            <div className="relative group">
              <span className="text-white hover:text-purple-300 transition-colors duration-300 font-medium flex items-center cursor-pointer">
                Services
                <span className="ml-2 text-sm">+</span>
              </span>
              
              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-80 bg-slate-900/95 backdrop-blur-sm border border-gray-700/50 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                <div className="py-4">
                  <Link
                    to="/corporate-services"
                    className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-sm font-medium normal-case tracking-normal"
                  >
                    Corporate Services
                  </Link>
                  <Link
                    to="/hr-services"
                    className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-sm font-medium normal-case tracking-normal"
                  >
                    Human Resource Management Services
                  </Link>
                  <Link
                    to="/accounting-services"
                    className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-sm font-medium normal-case tracking-normal"
                  >
                    Accounting Services
                  </Link>
                  <Link
                    to="/social-media-services"
                    className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-sm font-medium normal-case tracking-normal"
                  >
                    Social Media Presence Management
                  </Link>
                  <Link
                    to="/co-working-spaces"
                    className="block px-6 py-3 text-gray-300 hover:text-white hover:bg-purple-600/20 transition-all duration-200 text-sm font-medium normal-case tracking-normal"
                  >
                    Co-working Spaces
                  </Link>
                </div>
              </div>
            </div>
            
            <Link to="/contact" className="text-white hover:text-purple-300 transition-colors duration-300 font-medium">
              Contact
            </Link>
          </nav>



          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-white hover:text-purple-300 hover:bg-purple-600/20 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              aria-label="Toggle mobile menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}>
          <div className="border-t border-gray-800/50 bg-slate-900/95 backdrop-blur-sm">
            <nav className="flex flex-col px-2 py-4 space-y-1">
              <Link
                to="/"
                className="px-4 py-3 text-white hover:text-purple-300 hover:bg-purple-600/10 rounded-lg transition-all duration-300 font-medium font-AfacadFluxLight"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/about"
                className="px-4 py-3 text-white hover:text-purple-300 hover:bg-purple-600/10 rounded-lg transition-all duration-300 font-medium font-AfacadFluxLight"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              
              {/* Mobile Services Section */}
              <div className="px-4 py-2">
                <span className="text-white font-medium font-AfacadFluxLight text-sm uppercase tracking-wider">Services</span>
                <div className="mt-2 space-y-1">
                  <Link
                    to="/corporate-services"
                    className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-purple-600/10 rounded-md transition-all duration-300 text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Corporate Services
                  </Link>
                  <Link
                    to="/hr-services"
                    className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-purple-600/10 rounded-md transition-all duration-300 text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    HR Management Services
                  </Link>
                  <Link
                    to="/accounting-services"
                    className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-purple-600/10 rounded-md transition-all duration-300 text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Accounting Services
                  </Link>
                  <Link
                    to="/social-media-services"
                    className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-purple-600/10 rounded-md transition-all duration-300 text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Social Media Management
                  </Link>
                  <Link
                    to="/co-working-spaces"
                    className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-purple-600/10 rounded-md transition-all duration-300 text-sm"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Co-working Spaces
                  </Link>
                </div>
              </div>
              
              <Link
                to="/contact"
                className="px-4 py-3 text-white hover:text-purple-300 hover:bg-purple-600/10 rounded-lg transition-all duration-300 font-medium font-AfacadFluxLight"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 